import type { ReactNode } from 'react';

import type { Category } from './category-pill-group';

export enum SmartCreateFeedbackVariant {
	ROVO = 'rovo',
	CREATE_WITH_ROVO = 'create-with-rovo', // Confluence
	CREATE_WITH_ROVO_FROM_JIRA = 'create-with-rovo-from-jira', // Jira
	REMIX = 'remix',
	SLIDES = 'slides',
	CREATE_WITH_ROVO_FROM_JSM = 'create-with-rovo-from-jsm',
}

export interface SmartCreateFeedbackProps {
	/**
	 * The variant of the SmartCreateFeedback component (either "Rovo" or "Create with Rovo").
	 */
	variant?: SmartCreateFeedbackVariant;

	/**
	 * Callback function to close the SmartCreateFeedback component.
	 */
	onClose: () => void;

	/**
	 * Callback function to submit the feedback.
	 */
	onSubmit: () => void;

	/**
	 * The tenant ID (cloud ID) for the feedback context.
	 */
	tenantId?: string;

	/**
	 * Additional fields to log in the feedback context.  This will be saved, so do not share UGC here.
	 */
	additionalFields?: object;

	/**
	 * Optional extra content to render below the category pills in the feedback modal.
	 * Useful for adding custom fields (e.g. a feedback textarea, a "include conversation history" checkbox).
	 */
	customContent?: ReactNode;

	/**
	 * Whether to show the default summary and description text fields. Defaults to true.
	 * Set to false when providing custom text input via customContent.
	 */
	showDefaultTextFields?: boolean;

	/**
	 * Optional default value for the summary field.
	 */
	summaryDefaultValue?: string;

	/**
	 * Optional default value for the description field.
	 */
	descriptionDefaultValue?: string;

	/**
	 * Optional custom list of category pills to display in the "What category best describes your
	 * feedback?" section. When provided, replaces the default set of pills (Performance, Response
	 * quality, User experience, Other). Each entry must have a language-agnostic `id` and a
	 * pre-localised display `text`.
	 *
	 * This is intentionally kept caller-side so that different entry points (e.g. JSM space
	 * creation vs Confluence create-with-Rovo) can supply context-appropriate categories without
	 * modifying the shared component.
	 */
	categories?: Category[];
}
